import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CustomAvatar } from 'src/components/custom-avatar';
import { CustomTextField } from 'src/components/custom-input';
import { Iconify } from 'src/components/iconify';
import { HeaderIcon } from 'src/layouts/DashboardLayout/shared/styles';
import { useLocales } from 'src/locales';
import { GuestRequestType } from 'src/provider/VoiceChannel/livekit/types';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { useSelector } from 'src/redux/store';
import { GatheringRequests } from 'src/requests/gathering';
import { useVoiceChannelInviteModal } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelInvite/VoiceChannelInviteModal';
import { useChatAllowedAction } from 'src/sections/@dashboard/chat/shared/useChatAllowedAction';
import { VoiceChannelParticipants } from '../../../VoiceChannelParticipants';
import { RightMenuHeader } from '../../shared/styles';

const MenuContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  height: '100%',
  padding: theme.spacing(3, 2),
  gap: theme.spacing(3),
}));

export const GatheringParticipants = ({
  onClose,
  isGuest,
}: { onClose(): void; isGuest?: boolean }) => {
  const [searchMembersText, setSearchMembersText] = useState('');

  const { translate } = useLocales();

  const { inviteUsers } = useVoiceChannelInviteModal();

  const {
    connectionStatus,
    activeVoiceChannel,
    lobbyGuestsRequested,
    removeLobbyGuest,
  } = useLivekitRTCState();

  const channelsMap = useSelector((state) => state.chat.conversations.byId);

  const userPermissions = useChatAllowedAction({
    conversation: channelsMap[activeVoiceChannel.channel._id],
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      onClose();
    }
  }, [connectionStatus]);

  const searchResults = useMemo(() => {
    if (!searchMembersText) return activeVoiceChannel.members;
    return activeVoiceChannel.members.filter((member) =>
      member.user.name.toLowerCase().includes(searchMembersText.toLowerCase()),
    );
  }, [activeVoiceChannel.members, searchMembersText]);

  const handleAllow = async (guest: GuestRequestType) => {
    await GatheringRequests.allowGuestUser(guest.deviceId);
    removeLobbyGuest(guest);
  };

  const handleDeny = async (guest: GuestRequestType) => {
    await GatheringRequests.denyGuestUser(guest.deviceId);
    removeLobbyGuest(guest);
  };

  const handleAllowAll = async () => {
    lobbyGuestsRequested.forEach((guest) => handleAllow(guest));
  };
  const handleDenyAll = async () => {
    lobbyGuestsRequested.forEach((guest) => handleDeny(guest));
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <RightMenuHeader>
        <Stack direction="row" alignItems="center" gap={2}>
          <HeaderIcon>
            <Iconify icon="tabler:users" sx={{ color: 'primary.main' }} />
          </HeaderIcon>
          <Typography variant="h6">
            {translate('shared.labels.participants')}
          </Typography>
        </Stack>
        <IconButton onClick={onClose}>
          <Iconify icon="tabler:x" />
        </IconButton>
      </RightMenuHeader>

      <MenuContent>
        {!isGuest && !!lobbyGuestsRequested.length && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Typography variant="subtitle1">
                {translate('shared.labels.waiting_to_join')}
              </Typography>
              <Stack direction="row" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleDenyAll}
                >
                  {translate('shared.buttons.deny_all')}
                </Button>
                <Button variant="contained" onClick={handleAllowAll}>
                  {translate('shared.buttons.allow_all')}
                </Button>
              </Stack>
            </Stack>

            {lobbyGuestsRequested.map((guest) => (
              <Stack
                key={guest.deviceId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Stack direction="row" alignItems="center" gap={1} mr={1}>
                  <CustomAvatar
                    alt={guest.name}
                    name={guest.name}
                    src={guest.picture}
                    sx={{ height: 28, width: 28 }}
                  />
                  <Typography variant="body2">{guest.name}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleDeny(guest)}
                  >
                    {translate('shared.buttons.deny')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleAllow(guest)}
                  >
                    {translate('shared.buttons.allow')}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </>
        )}

        <Stack direction="row" alignItems="center" gap={2}>
          <CustomTextField
            fullWidth
            size="small"
            value={searchMembersText}
            onChange={(event) => setSearchMembersText(event.target.value)}
            placeholder={translate('shared.search.find_participants')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {!isGuest && (
            <Button
              variant="contained"
              onClick={inviteUsers}
              startIcon={<Iconify icon="tabler:users-plus" />}
              sx={{ flexShrink: 0 }}
            >
              {translate('shared.buttons.invite_participants')}
            </Button>
          )}
        </Stack>

        {searchMembersText && !searchResults.length && (
          <Stack spacing={1.5} direction="column" alignItems="center" p={3}>
            <Typography variant="subtitle1">
              {translate('workspace.share_member_list.no_members_found')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {translate('workspace.share_member_list.no_members_found_text')}
            </Typography>
          </Stack>
        )}

        <VoiceChannelParticipants
          conversationId={activeVoiceChannel.channel._id}
          participants={searchResults}
          isConnected={true}
          isAdmin={userPermissions?.manageMembers}
        />
      </MenuContent>
    </Stack>
  );
};
